import { useEffect, useRef, useState } from "react";
import { initialState, DataDisplayContext, DataDisplayState, DATA_DISPLAY_CHANGE_TYPE, DataDisplayFormDetails, DataDisplayAgent, DataDisplayClient, DataDisplayFormDetail, DataDisplayProduct, DataDisplayServiceAgent, DataDisplayServiceClient, DataDisplayAccountTransaction, DataDisplaySupplier, DataDisplayReceiver, DataDisplayPaymentAttachment, DataDisplayFile, ISectionRow } from "./data-display-context";
import type { FC, ReactNode } from "react";
import { useGetDisplayPreferenceTemplate } from "~/queries/useGetDisplayPreferenceTemplate";
import { useMe } from "~/queries/useMe";
import { useCreateOrUpdateDisplayPreference } from "~/queries/useCreateOrUpdateDisplayPreference";
import { DISPLAY_PREFERENCE_TYPE } from "~/types/__generated/gql/graphql";
import toast from "react-hot-toast";
interface DataDisplayProviderProps {
  children?: ReactNode;
}
export const DataDisplayProvider: FC<DataDisplayProviderProps> = props => {
  const {
    children
  } = props;
  const [state, setState] = useState<DataDisplayState>(initialState);
  const agentListSettingsRef = useRef(state.agent.list);
  const clientListSettingsRef = useRef(state.client.list);
  const createOrUpdateDisplayPreference = useCreateOrUpdateDisplayPreference();
  const {
    data: template
  } = useGetDisplayPreferenceTemplate();
  const {
    data: me
  } = useMe();
  useEffect(() => {
    agentListSettingsRef.current = state.agent.list;
  }, [state.agent.list]);
  useEffect(() => {
    clientListSettingsRef.current = state.client.list;
  }, [state.client.list]);
  useEffect(() => {
    if (me?._displayPreference) {
      if (template) {
        const agent = combineDisplayPreference({
          ...initialState.agent,
          ...(state.agent.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.agent as unknown as DataDisplayAgent)
        }, {
          ...initialState.agent,
          ...(state.agent.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(template.agent as unknown as DataDisplayClient)
        }, true);
        const client = combineDisplayPreference({
          ...initialState.client,
          ...(state.client.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.client as unknown as DataDisplayAgent)
        }, {
          ...initialState.client,
          ...(state.client.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(template.client as unknown as DataDisplayClient)
        }, true);
        const supplier = combineDisplayPreference({
          ...initialState.supplier,
          ...(state.supplier.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.supplier as unknown as DataDisplaySupplier)
        }, {
          ...initialState.supplier,
          ...(state.supplier.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(template.supplier as unknown as DataDisplaySupplier)
        }, false);
        const receiver = combineDisplayPreference({
          ...initialState.receiver,
          ...(state.receiver.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.receiver as unknown as DataDisplayReceiver)
        }, {
          ...initialState.receiver,
          ...(state.receiver.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(template.receiver as unknown as DataDisplayReceiver)
        }, false);
        const product = {
          ...initialState.product,
          ...(state.product.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.product as unknown as DataDisplayProduct)
        };
        const serviceAgent = {
          ...initialState.serviceAgent,
          ...(state.serviceAgent.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.serviceAgent as unknown as DataDisplayServiceAgent)
        };
        const serviceClient = {
          ...initialState.serviceClient,
          ...(state.serviceClient.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.serviceClient as unknown as DataDisplayServiceClient)
        };
        const accountTransaction = {
          ...initialState.accountTransaction,
          ...(state.accountTransaction.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.accountTransaction as unknown as DataDisplayAccountTransaction)
        };
        const paymentAttachment = {
          ...initialState.paymentAttachment,
          ...(state.paymentAttachment.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.paymentAttachment as unknown as DataDisplayPaymentAttachment)
        };
        const file = combineFileDisplayPreference({
          ...initialState.file,
          ...(state.file.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(me._displayPreference.file as unknown as DataDisplayFile)
        }, {
          ...initialState.file,
          ...(state.file.isListModelOpen === true ? {
            isListModelOpen: true
          } : undefined),
          ...(template.file as unknown as DataDisplayFile)
        });
        return setState({
          agent,
          client,
          supplier,
          receiver,
          product,
          serviceAgent,
          serviceClient,
          accountTransaction,
          paymentAttachment,
          file
        });
      } else {
        return setState({
          agent: {
            ...initialState.agent,
            ...(state.agent.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.agent as unknown as DataDisplayAgent)
          },
          client: {
            ...initialState.client,
            ...(state.client.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.client as unknown as DataDisplayClient)
          },
          supplier: {
            ...initialState.supplier,
            ...(state.supplier.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.supplier as unknown as DataDisplaySupplier)
          },
          receiver: {
            ...initialState.receiver,
            ...(state.receiver.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.receiver as unknown as DataDisplayReceiver)
          },
          product: {
            ...initialState.product,
            ...(state.product.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.product as unknown as DataDisplayProduct)
          },
          serviceAgent: {
            ...initialState.serviceAgent,
            ...(state.serviceAgent.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.serviceAgent as unknown as DataDisplayServiceAgent)
          },
          serviceClient: {
            ...initialState.serviceClient,
            ...(state.serviceClient.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.serviceClient as unknown as DataDisplayServiceClient)
          },
          accountTransaction: {
            ...initialState.accountTransaction,
            ...(state.accountTransaction.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.accountTransaction as unknown as DataDisplayAccountTransaction)
          },
          paymentAttachment: {
            ...initialState.paymentAttachment,
            ...(state.paymentAttachment.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.paymentAttachment as unknown as DataDisplayPaymentAttachment)
          },
          file: {
            ...initialState.file,
            ...(state.file.isListModelOpen === true ? {
              isListModelOpen: true
            } : undefined),
            ...(me._displayPreference.file as unknown as DataDisplayFile)
          }
        });
      }
    }
    if (template) {
      return setState({
        agent: {
          ...initialState.agent,
          ...(template.agent as unknown as DataDisplayAgent)
        },
        client: {
          ...initialState.client,
          ...(template.client as unknown as DataDisplayClient)
        },
        supplier: {
          ...initialState.supplier,
          ...(template.supplier as unknown as DataDisplaySupplier)
        },
        receiver: {
          ...initialState.receiver,
          ...(template.receiver as unknown as DataDisplayReceiver)
        },
        product: {
          ...initialState.product,
          ...(template.product as unknown as DataDisplayProduct)
        },
        serviceAgent: {
          ...initialState.serviceAgent,
          ...(template.serviceAgent as unknown as DataDisplayServiceAgent)
        },
        serviceClient: {
          ...initialState.serviceClient,
          ...(template.serviceClient as unknown as DataDisplayServiceClient)
        },
        accountTransaction: {
          ...initialState.accountTransaction,
          ...(template.accountTransaction as unknown as DataDisplayAccountTransaction)
        },
        paymentAttachment: {
          ...initialState.paymentAttachment,
          ...(template.paymentAttachment as unknown as DataDisplayPaymentAttachment)
        },
        file: {
          ...initialState.file,
          ...(state.file.isFormModelOpen === true ? {
            isFormModelOpen: true
          } : undefined),
          ...(template.file as unknown as DataDisplayFile)
        }
        // if no template and no user preference, will use the initial state which is already set
      });
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, template]);
  const handleAgentFormModelOpen = () => {
    const newState = {
      ...state,
      agent: {
        ...state.agent,
        isFormModelOpen: true
      }
    };
    setState(newState);
  };
  const handleAgentFormModelClose = () => {
    const newState = {
      ...state,
      agent: {
        ...state.agent,
        isFormModelOpen: false
      }
    };
    setState(newState);
  };
  const handleAgentListModelOpen = () => {
    const newState = {
      ...state,
      agent: {
        ...state.agent,
        isListModelOpen: true
      }
    };
    setState(newState);
  };
  const handleAgentListModelClose = () => {
    const newState = {
      ...state,
      agent: {
        ...state.agent,
        isListModelOpen: false
      }
    };
    setState(newState);
  };
  const handleChangeAgentListSettings = async (change: any[], templateType: DISPLAY_PREFERENCE_TYPE | null = null) => {
    const newState = {
      ...state,
      agent: {
        ...state.agent,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState, templateType);
    agentListSettingsRef.current = change;
    setState(newState);
  };
  const handleChangeAgentFormSettings = async (type: DATA_DISPLAY_CHANGE_TYPE, change: any[]) => {
    const newState = {
      ...state,
      agent: {
        ...state.agent,
        list: agentListSettingsRef.current,
        form: {
          ...state.agent.form,
          [`${type}`]: change
        }
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleClientFormModelOpen = () => {
    const newState = {
      ...state,
      client: {
        ...state.client,
        isFormModelOpen: true
      }
    };
    setState(newState);
  };
  const handleClientFormModelClose = () => {
    const newState = {
      ...state,
      client: {
        ...state.client,
        isFormModelOpen: false
      }
    };
    setState(newState);
  };
  const handleClientListModelOpen = () => {
    const newState = {
      ...state,
      client: {
        ...state.client,
        isListModelOpen: true
      }
    };
    setState(newState);
  };
  const handleClientListModelClose = () => {
    const newState = {
      ...state,
      client: {
        ...state.client,
        isListModelOpen: false
      }
    };
    setState(newState);
  };
  const handleChangeClientListSettings = async (change: any[], templateType: DISPLAY_PREFERENCE_TYPE | null = null) => {
    const newState = {
      ...state,
      client: {
        ...state.client,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState, templateType);
    clientListSettingsRef.current = change;
    setState(newState);
  };
  const handleChangeClientFormSettings = async (type: DATA_DISPLAY_CHANGE_TYPE, change: any[]) => {
    const newState = {
      ...state,
      client: {
        ...state.client,
        list: clientListSettingsRef.current,
        form: {
          ...state.client.form,
          [`${type}`]: change
        }
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleProductListModelOpen = () => {
    const newState = {
      ...state,
      product: {
        ...state.product,
        isListModelOpen: true
      }
    };
    setState(newState);
  };
  const handleProductListModelClose = () => {
    const newState = {
      ...state,
      product: {
        ...state.product,
        isListModelOpen: false
      }
    };
    setState(newState);
  };
  const handleChangeProductListSettings = async (change: any[]) => {
    const newState = {
      ...state,
      product: {
        ...state.product,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleChangeServiceAgentListSettings = async (change: any[]) => {
    const newState = {
      ...state,
      serviceAgent: {
        ...state.serviceAgent,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleChangeServiceClientListSettings = async (change: any[]) => {
    const newState = {
      ...state,
      serviceClient: {
        ...state.serviceClient,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleSupplierFormModelOpen = () => {
    const newState = {
      ...state,
      supplier: {
        ...state.supplier,
        isFormModelOpen: true
      }
    };
    setState(newState);
  };
  const handleSupplierFormModelClose = () => {
    const newState = {
      ...state,
      supplier: {
        ...state.supplier,
        isFormModelOpen: false
      }
    };
    setState(newState);
  };
  const handleSupplierListModelOpen = () => {
    const newState = {
      ...state,
      supplier: {
        ...state.supplier,
        isListModelOpen: true
      }
    };
    setState(newState);
  };
  const handleSupplierListModelClose = () => {
    const newState = {
      ...state,
      supplier: {
        ...state.supplier,
        isListModelOpen: false
      }
    };
    setState(newState);
  };
  const handleChangeSupplierListSettings = async (change: any[]) => {
    const newState = {
      ...state,
      supplier: {
        ...state.supplier,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleChangeSupplierFormSettings = async (type: DATA_DISPLAY_CHANGE_TYPE, change: any[]) => {
    const newState = {
      ...state,
      supplier: {
        ...state.supplier,
        form: {
          ...state.supplier.form,
          [`${type}`]: change
        }
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleReceiverFormModelOpen = () => {
    const newState = {
      ...state,
      receiver: {
        ...state.receiver,
        isFormModelOpen: true
      }
    };
    setState(newState);
  };
  const handleReceiverFormModelClose = () => {
    const newState = {
      ...state,
      receiver: {
        ...state.receiver,
        isFormModelOpen: false
      }
    };
    setState(newState);
  };
  const handleReceiverListModelOpen = () => {
    const newState = {
      ...state,
      receiver: {
        ...state.receiver,
        isListModelOpen: true
      }
    };
    setState(newState);
  };
  const handleReceiverListModelClose = () => {
    const newState = {
      ...state,
      receiver: {
        ...state.receiver,
        isListModelOpen: false
      }
    };
    setState(newState);
  };
  const handleChangeReceiverListSettings = async (change: any[]) => {
    const newState = {
      ...state,
      receiver: {
        ...state.receiver,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleChangeReceiverFormSettings = async (type: DATA_DISPLAY_CHANGE_TYPE, change: any[]) => {
    const newState = {
      ...state,
      receiver: {
        ...state.receiver,
        form: {
          ...state.receiver.form,
          [`${type}`]: change
        }
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleAccountTransactionFormModelOpen = () => {
    const newState = {
      ...state,
      accountTransaction: {
        ...state.accountTransaction,
        isFormModelOpen: true
      }
    };
    setState(newState);
  };
  const handleAccountTransactionFormModelClose = () => {
    const newState = {
      ...state,
      accountTransaction: {
        ...state.accountTransaction,
        isFormModelOpen: false
      }
    };
    setState(newState);
  };
  const handleAccountTransactionListModelOpen = () => {
    const newState = {
      ...state,
      accountTransaction: {
        ...state.accountTransaction,
        isListModelOpen: true
      }
    };
    setState(newState);
  };
  const handleAccountTransactionListModelClose = () => {
    const newState = {
      ...state,
      accountTransaction: {
        ...state.accountTransaction,
        isListModelOpen: false
      }
    };
    setState(newState);
  };
  const handleChangeAccountTransactionListSettings = async (change: any[]) => {
    const newState = {
      ...state,
      accountTransaction: {
        ...state.accountTransaction,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleChangeAccountTransactionFormSettings = async (type: DATA_DISPLAY_CHANGE_TYPE, change: any[]) => {
    const newState = {
      ...state,
      accountTransaction: {
        ...state.accountTransaction,
        isFormModelOpen: false,
        form: {
          ...state.accountTransaction.form,
          [`${type}`]: change
        }
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handlePaymentAttachmentFormModelOpen = () => {
    const newState = {
      ...state,
      paymentAttachment: {
        ...state.paymentAttachment,
        isFormModelOpen: true
      }
    };
    setState(newState);
  };
  const handlePaymentAttachmentFormModelClose = () => {
    const newState = {
      ...state,
      paymentAttachment: {
        ...state.paymentAttachment,
        isFormModelOpen: false
      }
    };
    setState(newState);
  };
  const handlePaymentAttachmentListModelOpen = () => {
    const newState = {
      ...state,
      paymentAttachment: {
        ...state.paymentAttachment,
        isListModelOpen: true
      }
    };
    setState(newState);
  };
  const handlePaymentAttachmentListModelClose = () => {
    const newState = {
      ...state,
      paymentAttachment: {
        ...state.paymentAttachment,
        isListModelOpen: false
      }
    };
    setState(newState);
  };
  const handleChangePaymentAttachmentListSettings = async (change: any[]) => {
    const newState = {
      ...state,
      paymentAttachment: {
        ...state.paymentAttachment,
        list: change
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleChangePaymentAttachmentFormSettings = async (type: DATA_DISPLAY_CHANGE_TYPE, change: any[]) => {
    const newState = {
      ...state,
      paymentAttachment: {
        ...state.paymentAttachment,
        isFormModelOpen: false,
        form: {
          ...state.paymentAttachment.form,
          [`${type}`]: change
        }
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleFileFormModelOpen = () => {
    setState({
      ...state,
      file: {
        ...state.file,
        isFormModelOpen: true
      }
    });
  };
  const handleFileFormModelClose = () => {
    setState({
      ...state,
      file: {
        ...state.file,
        isFormModelOpen: false
      }
    });
  };
  const handleChangeFileFormSettings = async (_type: DATA_DISPLAY_CHANGE_TYPE, change: any[]) => {
    const newState = {
      ...state,
      file: {
        ...state.file,
        isFormModelOpen: false,
        form: {
          ...state.file.form,
          sections: change
        }
      }
    };
    await handleUpdateDisplayPreference(newState);
    setState(newState);
  };
  const handleUpdateDisplayPreference = async (state: DataDisplayState, templateType: DISPLAY_PREFERENCE_TYPE | null = null) => {
    await toast.promise(
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
      try {
        await createOrUpdateDisplayPreference.mutateAsync({
          input: {
            templateType,
            agent: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.AGENT,
              list: state.agent.list,
              form: state.agent.form
            },
            client: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.CLIENT,
              list: state.client.list,
              form: state.client.form
            },
            supplier: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.SUPPLIER,
              list: state.supplier.list,
              form: state.supplier.form
            },
            receiver: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.RECEIVER,
              list: state.receiver.list,
              form: state.receiver.form
            },
            product: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.PRODUCT,
              list: state.product.list
            },
            serviceAgent: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.SERVICE_AGENT,
              list: state.serviceAgent.list
            },
            serviceClient: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.SERVICE_CLIENT,
              list: state.serviceClient.list
            },
            accountTransaction: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.ACCOUNT_TRANSACTION,
              list: state.accountTransaction.list,
              form: state.accountTransaction.form
            },
            paymentAttachment: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.PAYMENT_ATTACHMENT,
              list: state.paymentAttachment.list,
              form: state.paymentAttachment.form
            },
            file: {
              isTemplate: false,
              type: DISPLAY_PREFERENCE_TYPE.FILE,
              list: state.file.list,
              form: state.file.form
            }
            // serviceAgent and serviceClient are using agent and client list/form because they are the same for now, will be different in the future when we have service form
          }
        });
        resolve(null);
      } catch (e) {
        reject(e instanceof Error ? e : new Error("Something went wrong"));
      }
    }), {
      loading: "Updating display preference...",
      success: "Display preference update successfully",
      error: "Failed to upload display preference"
    });
  };
  const combineDisplayPreference = (user: DataDisplayAgent | DataDisplayClient | DataDisplaySupplier | DataDisplayReceiver, template: DataDisplayAgent | DataDisplayClient | DataDisplaySupplier | DataDisplayReceiver, hasService: boolean) => {
    // handle info section
    const userInfo = user.form.info.flatMap(i => i.fields);
    const templateInfo = template.form.info.flatMap(i => i.fields);
    const extraInfo = templateInfo.filter(i => !userInfo.find(u => u.fieldName === i.fieldName));
    let combinedInfo = user.form.info;
    if (userInfo.length !== templateInfo.length) {
      combinedInfo = getCustomFieldForm(extraInfo, user.form.info);
    }

    // handle address section
    const userAddr = user.form.addresses.flatMap(i => i.fields);
    const templateAddr = template.form.addresses.flatMap(i => i.fields);
    const extraAddr = templateInfo.filter(i => !userInfo.find(u => u.fieldName === i.fieldName));
    let combinedAddr = user.form.addresses;
    if (userAddr.length !== templateAddr.length) {
      combinedAddr = getCustomFieldForm(extraAddr, user.form.addresses, "addresses");
    }

    // handle contact section
    const userCont = user.form.contacts.flatMap(i => i.fields);
    const templateCont = template.form.contacts.flatMap(i => i.fields);
    const extraCont = templateInfo.filter(i => !userInfo.find(u => u.fieldName === i.fieldName));
    let combinedCont = user.form.contacts;
    if (userCont.length !== templateCont.length) {
      combinedCont = getCustomFieldForm(extraCont, user.form.contacts, "contacts");
    }
    return {
      ...template,
      list: user.list && user.list.length > 0 ? user.list : template.list && template.list.length > 0 ? template.list : [],
      form: {
        info: combinedInfo,
        addresses: combinedAddr,
        contacts: combinedCont,
        ...(hasService ? {
          service: initialState.agent.form.service
        } : undefined)
      }
    } as unknown as DataDisplayAgent;
  };
  const combineFileDisplayPreference = (user: DataDisplayFile, template: DataDisplayFile): DataDisplayFile => {
    const userBasicInfo = user.form.sections.find(section => section.name === "Basic Information");
    const templateBasicInfo = template.form.sections.find(section => section.name === "Basic Information");
    let sections = user.form.sections;
    if (userBasicInfo && templateBasicInfo) {
      const userFieldIds = new Set(userBasicInfo.rows.flatMap(row => row.fields.map(field => field.id)));
      const extraFields = templateBasicInfo.rows.flatMap(row => row.fields).filter(field => field.id.startsWith("basic-info-custom-fields-") && !userFieldIds.has(field.id));
      if (extraFields.length > 0) {
        let rowIndex = Math.max(-1, ...userBasicInfo.rows.map(row => row.sortingIndex)) + 1;
        let fieldIndex = Math.max(-1, ...userBasicInfo.rows.flatMap(row => row.fields.map(field => field.sortingIndex))) + 1;
        const extraRows: ISectionRow[] = [];
        for (let i = 0; i < extraFields.length; i += 4) {
          extraRows.push({
            sortingIndex: rowIndex++,
            fields: extraFields.slice(i, i + 4).map(field => ({
              ...field,
              sortingIndex: fieldIndex++
            }))
          });
        }
        sections = user.form.sections.map(section => section.name === "Basic Information" ? {
          ...section,
          rows: [...section.rows, ...extraRows]
        } : section);
      }
    } else if (!userBasicInfo && templateBasicInfo) {
      sections = [...user.form.sections, templateBasicInfo];
    }
    return {
      ...template,
      ...user,
      list: user.list ?? [],
      form: {
        ...template.form,
        ...user.form,
        sections
      }
    };
  };
  const getCustomFieldForm = (extra: DataDisplayFormDetail[], form: DataDisplayFormDetails[], parentFieldName?: string) => {
    let returnArr = form;
    let rowIndex = form.length;
    let formIndex = form.map(f => f.fields).flat().length;
    for (let i = 0; i < extra.length; i += 4) {
      const customFields = {
        sortingIndex: rowIndex,
        fields: extra.slice(i, i + 4).map(c => {
          return {
            parentFieldName,
            fieldName: c.fieldName,
            fieldLabel: c.fieldLabel,
            fieldType: c.fieldType,
            sortingIndex: formIndex++,
            isCustomField: true,
            isPinned: c.isPinned
          };
        })
      };
      returnArr = [...returnArr, customFields];
      rowIndex++;
    }
    return returnArr;
  };
  return <DataDisplayContext.Provider value={{
    ...state,
    handleAgentFormModelOpen,
    handleAgentFormModelClose,
    handleAgentListModelOpen,
    handleAgentListModelClose,
    handleChangeAgentListSettings,
    handleChangeAgentFormSettings,
    handleClientFormModelOpen,
    handleClientFormModelClose,
    handleClientListModelOpen,
    handleClientListModelClose,
    handleChangeClientListSettings,
    handleChangeClientFormSettings,
    handleProductListModelOpen,
    handleProductListModelClose,
    handleChangeProductListSettings,
    handleChangeServiceAgentListSettings,
    handleChangeServiceClientListSettings,
    handleSupplierFormModelOpen,
    handleSupplierFormModelClose,
    handleSupplierListModelOpen,
    handleSupplierListModelClose,
    handleChangeSupplierListSettings,
    handleChangeSupplierFormSettings,
    handleReceiverFormModelOpen,
    handleReceiverFormModelClose,
    handleReceiverListModelOpen,
    handleReceiverListModelClose,
    handleChangeReceiverListSettings,
    handleChangeReceiverFormSettings,
    handleAccountTransactionFormModelOpen,
    handleAccountTransactionFormModelClose,
    handleAccountTransactionListModelOpen,
    handleAccountTransactionListModelClose,
    handleChangeAccountTransactionListSettings,
    handleChangeAccountTransactionFormSettings,
    handlePaymentAttachmentFormModelOpen,
    handlePaymentAttachmentFormModelClose,
    handlePaymentAttachmentListModelOpen,
    handlePaymentAttachmentListModelClose,
    handleChangePaymentAttachmentListSettings,
    handleChangePaymentAttachmentFormSettings,
    handleFileFormModelOpen,
    handleFileFormModelClose,
    handleChangeFileFormSettings
  }} data-sentry-element="unknown" data-sentry-component="DataDisplayProvider" data-sentry-source-file="data-display-provider.tsx">
			{children}
		</DataDisplayContext.Provider>;
};